import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet } from '../../shared/components/BottomSheet';
import { getCustomerStateTransitions } from '../../customer-states/api/customer-states.api';
import { useChangeCustomerState } from '../hooks/useChangeCustomerState';
import { toast } from 'react-toastify';
import type { CustomerState } from '../../customer-states/types/customer-states.types';

interface CustomerStateBottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    currentStateId: string;
    currentState?: CustomerState;
    allStates: CustomerState[];
    onSuccess: (newState: CustomerState) => void;
}

type ViewState = 'select' | 'confirm';

export const CustomerStateBottomSheet: React.FC<CustomerStateBottomSheetProps> = ({
    isOpen,
    onClose,
    customerId,
    currentStateId,
    currentState,
    allStates,
    onSuccess
}) => {
    const [viewState, setViewState] = useState<ViewState>('select');
    const [allowedTransitionIds, setAllowedTransitionIds] = useState<string[]>([]);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [selectedState, setSelectedState] = useState<CustomerState | null>(null);

    const { mutate, isLoading } = useChangeCustomerState();

    const loadStates = useCallback(async () => {
        setIsFetchingData(true);
        try {
            const transitions = await getCustomerStateTransitions(currentStateId);
            setAllowedTransitionIds(transitions || []);
        } catch (error) {
            console.error("Error fetching state data:", error);
            toast.error('Error al cargar los estados disponibles');
        } finally {
            setIsFetchingData(false);
        }
    }, [currentStateId]);

    // Reset view when sheet opens
    useEffect(() => {
        if (isOpen) {
            setViewState('select');
            setSelectedState(null);
            loadStates();
        }
    }, [isOpen, currentStateId, loadStates]);

    const getStateColor = (type?: string): string => {
        switch (type) {
            case 'INITIAL': return '#00a884';
            case 'FINAL': return '#ef5350';
            case 'TRANSIENT': return '#ff9800';
            default: return '#667781';
        }
    };

    const handleStateSelect = (state: CustomerState) => {
        setSelectedState(state);
        setViewState('confirm');
    };

    const handleConfirm = async () => {
        if (!selectedState) return;

        try {
            await mutate(customerId, selectedState.id);
            toast.success('Estado actualizado exitosamente');
            onSuccess(selectedState);
            onClose();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Error al cambiar el estado';
            toast.error(errorMessage);
            setViewState('select');
            setSelectedState(null);
        }
    };

    const handleCancel = () => {
        setViewState('select');
        setSelectedState(null);
    };

    const allowedStates = allStates.filter(
        s => allowedTransitionIds.includes(s.id) && s.id !== currentStateId
    );

    return (
        <BottomSheet isOpen={isOpen} onClose={onClose} height="50%">
            {viewState === 'select' ? (
                <>
                    {/* Current State Display */}
                    <div className="mb-4">
                        <h6 className="text-muted mb-2" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                            Estado Actual
                        </h6>
                        {(currentState || allStates.find(s => s.id === currentStateId)) && (
                            <div
                                className="d-flex align-items-center gap-2 p-3 rounded"
                                style={{
                                    backgroundColor: `${getStateColor(currentState?.type || allStates.find(s => s.id === currentStateId)?.type)}15`,
                                    border: `1px solid ${getStateColor(currentState?.type || allStates.find(s => s.id === currentStateId)?.type)}30`
                                }}
                            >
                                <div
                                    className="rounded-circle flex-shrink-0"
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: getStateColor(currentState?.type || allStates.find(s => s.id === currentStateId)?.type)
                                    }}
                                />
                                <span className="fw-medium" style={{ color: getStateColor(currentState?.type || allStates.find(s => s.id === currentStateId)?.type) }}>
                                    {currentState?.name || allStates.find(s => s.id === currentStateId)?.name}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Available States */}
                    <div>
                        <h6 className="text-muted mb-2" style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                            Cambiar a
                        </h6>
                        {isFetchingData ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-muted" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                        ) : allowedStates.length > 0 ? (
                            <div className="d-flex flex-column gap-2">
                                {allowedStates.map((state) => (
                                    <button
                                        key={state.id}
                                        className="btn d-flex align-items-center gap-3 p-3 text-start border"
                                        style={{
                                            minHeight: '44px',
                                            backgroundColor: 'white',
                                            borderColor: '#dee2e6',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                                        onClick={() => handleStateSelect(state)}
                                        disabled={isLoading}
                                    >
                                        <div
                                            className="rounded-circle flex-shrink-0"
                                            style={{ width: '12px', height: '12px', backgroundColor: getStateColor(state.type) }}
                                        />
                                        <span className="flex-grow-1 fw-medium">{state.name}</span>
                                        <i className="ri-arrow-right-s-line text-muted fs-5"></i>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-3 text-muted" style={{ fontSize: '0.875rem' }}>
                                No hay transiciones disponibles
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {/* Confirmation View */}
                    <div className="text-center py-4">
                        <div
                            className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                            style={{
                                width: '60px',
                                height: '60px',
                                backgroundColor: `${getStateColor(selectedState?.type)}15`,
                                border: `2px solid ${getStateColor(selectedState?.type)}30`
                            }}
                        >
                            <i className="ri-git-branch-line fs-24" style={{ color: getStateColor(selectedState?.type) }}></i>
                        </div>
                        <h5 className="mb-2">¿Confirmar cambio?</h5>
                        <p className="text-muted mb-4">
                            ¿Deseas cambiar el estado a <strong>{selectedState?.name}</strong>?
                        </p>

                        <div className="d-flex gap-2">
                            <button
                                className="btn bg-light fw-medium flex-fill"
                                style={{ minHeight: '48px' }}
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                No
                            </button>
                            <button
                                className="btn btn-primary fw-medium flex-fill"
                                style={{ minHeight: '48px' }}
                                onClick={handleConfirm}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                ></span>
                                        Cambiando...
                                    </>
                                ) : (
                                    'Sí'
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </BottomSheet>
    );
};
